// The campaign wizard (W1, planner/partner-campaign/campaign-wizard-plan-2026-10-29.md): a campaign is made in 6
// steps (basics → numbers + limits → content → audience → canvas → schedule) and saved as a draft on every step.
// "Send for approval" checks every step again; the approver sees the same summary and the canvas (read only).
// The campaign keeps its own canvas (a WorkflowDocument): built from the steps, or started from a saved workflow
// (A4). The audience, the header image and the voice message change per campaign (A7); a copy keeps the rest (A11).
import { z } from 'zod'
import { WorkflowDocument } from '@the-kit/contracts'
import { Id, IsoDateTime, LeadCode, MaskedMobile } from './common'
import { CampaignKind, CampaignNumbers, CampaignSchedule, CampaignStatus, NumberCaps } from './campaigns'
import { ButtonValue, TemplateValue } from './templates'

export const WIZARD_STEPS = ['basics', 'numbers', 'content', 'audience', 'canvas', 'schedule'] as const
export const WizardStep = z.enum(WIZARD_STEPS)
export type WizardStep = z.infer<typeof WizardStep>

/** The channels a campaign can use (step 1). The lender is a step, not a way to reach a person. */
export const WizardChannel = z.enum(['ivr', 'whatsapp', 'lender', 'sms'])
export type WizardChannel = z.infer<typeof WizardChannel>
export const SENDING_CHANNELS: readonly WizardChannel[] = ['ivr', 'whatsapp', 'sms']

export const IvrDigit = z.enum(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'])
export type IvrDigit = z.infer<typeof IvrDigit>

/** What a key does (A2): any digit 0–9; one is "Stop" (law + WhatsApp quality), at most one "Consent yes" (A6). */
export const IvrKeyAction = z.enum(['consent_yes', 'whatsapp', 'lender', 'sms', 'again', 'stop', 'end'])
export type IvrKeyAction = z.infer<typeof IvrKeyAction>
export const MAX_IVR_KEYS = 5
/** The channel an action needs ticked in step 1 (consent, again, stop and end need none). */
export const KEY_ACTION_CHANNEL: Partial<Record<IvrKeyAction, WizardChannel>> = {
  whatsapp: 'whatsapp',
  lender: 'lender',
  sms: 'sms',
}

export const IvrKey = z.object({ digit: IvrDigit, action: IvrKeyAction })
export type IvrKey = z.infer<typeof IvrKey>

export const MAX_IVR_TRIES = 3
export const IvrContent = z.object({
  message_id: z.string().describe('an approved voice message; changes per campaign (A7)'),
  keys: z.array(IvrKey).max(MAX_IVR_KEYS, `At most ${MAX_IVR_KEYS} keys`),
  retry_hours: z.number('Type hours.').int().min(1, 'At least 1 hour').max(24, 'At most 24 hours'),
  max_tries: z
    .number('Type a number.')
    .int()
    .min(1, 'At least 1 try')
    .max(MAX_IVR_TRIES, `At most ${MAX_IVR_TRIES} tries`),
})
export type IvrContent = z.infer<typeof IvrContent>

/** The template's header image: its own, a public https URL, or an image from the Images page (R3). */
export const HeaderSource = z.enum(['template', 'url', 'image'])
export type HeaderSource = z.infer<typeof HeaderSource>
export const HEADER_URL = /^https:\/\/\S+\.(jpe?g|png|webp)(\?\S*)?$/i

/** L1 (2026-11-03): an Apply now journey as in its address, e.g. banxx-offers (/apply-now/banxx-offers). */
export const JourneySlug = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,59}$/, 'The journey as in its address, e.g. banxx-offers')

/** Campaign links (owner 2026-11-05): an App admin campaign link's code as in its short link, e.g. BHT1F6. */
export const AppLinkCode = z.string().regex(/^[A-Za-z0-9]{4,12}$/, "The App admin link's code, e.g. BHT1F6")

export const WhatsAppContent = z.object({
  template_id: z.string(),
  header_source: HeaderSource,
  header_url: z.string().describe('header_source url: a public https link to a JPG, PNG or WebP'),
  header_image_id: z.string().describe('header_source image: an image of the Images page'),
  value_1: TemplateValue,
  value_2: TemplateValue,
  value_3: TemplateValue,
  value_4: TemplateValue,
  value_5: TemplateValue,
  fixed_text: z.string().max(60, 'At most 60 letters'),
  fallback: z.string().max(30, 'At most 30 letters'),
  button_value: ButtonValue.optional().describe("PD61: what fills the button's {{1}}; none = the short link"),
  button_text: z
    .string()
    .max(60, 'At most 60 letters')
    .optional()
    .describe('button_value fixed_text: the end of the button address, e.g. offers'),
  link_journey: JourneySlug.optional().describe(
    'L1: the short link opens this Apply now journey (/apply-now/<journey>); none = the Apply now start'
  ),
  link_campaign: AppLinkCode.optional().describe(
    "the short link opens this App admin campaign link (apply.<domain>/l/<code>, with utm_content = the lead code): the tap counts here and on that link, the lead counts on that link; not together with link_journey"
  ),
})
export type WhatsAppContent = z.infer<typeof WhatsAppContent>

/** A Hub route (dedupe / prefill / link), or the lender's own UTM link from the Hub with no API call (R2). */
export const LenderMode = z.enum(['route', 'link_only'])
export type LenderMode = z.infer<typeof LenderMode>
export const LenderContent = z.object({
  mode: LenderMode,
  lender_slug: z.string(),
  route_id: z.string(),
  link_wait_hours: z
    .number()
    .int()
    .min(0)
    .max(72)
    .optional()
    .describe(
      "W4: a prefill whose link comes later is asked again every 10 minutes for up to this many hours (0 / none = don't wait)"
    ),
})
export type LenderContent = z.infer<typeof LenderContent>

export const SmsContent = z.object({ template_id: z.string() })
export type SmsContent = z.infer<typeof SmsContent>

/** Step 3: one part per ticked channel (null = not ticked). */
export const CampaignContent = z.object({
  ivr: IvrContent.nullable(),
  whatsapp: WhatsAppContent.nullable(),
  lender: LenderContent.nullable(),
  sms: SmsContent.nullable(),
})
export type CampaignContent = z.infer<typeof CampaignContent>

/** Now = 5 minutes after the approver approves (A1); later = a date and hour; daily = a batch / group schedule. */
export const StartMode = z.enum(['now', 'later', 'daily'])
export type StartMode = z.infer<typeof StartMode>
export const NOW_DELAY_MINUTES = 5

/** What a draft holds; saved on every step, so nothing here has to be complete yet. */
export const CampaignDraftInput = z.object({
  name: z.string().trim().max(80, 'At most 80 letters'),
  kind: CampaignKind,
  channels: z.array(WizardChannel),
  numbers: CampaignNumbers,
  number_caps: NumberCaps,
  content: CampaignContent,
  audience_id: Id.nullable(),
  schedule: CampaignSchedule.nullable(),
  people_limit: z.number().int().min(1).nullable().describe('newest leads first (A5); null = everyone'),
  include_no_consent: z
    .boolean()
    .optional()
    .describe('PD63: a marketing campaign also reaches people with no consent tag yet (default off)'),
  document: WorkflowDocument.nullable().describe('the canvas (step 5); null until it is opened'),
  canvas_steps: z
    .string()
    .describe(
      'the steps 1–4 choices the canvas was built from; the wizard offers a rebuild when they change'
    ),
  workflow_id: Id.nullable().describe('the saved workflow the canvas started from or was saved as'),
  start_mode: StartMode,
  start_at: IsoDateTime.nullable().describe('later: when it starts'),
})
export type CampaignDraftInput = z.infer<typeof CampaignDraftInput>

/** What a copy (A11) changed from the campaign it was copied from; the approver sees it marked (R5). */
export const CopyChange = z.enum(['audience', 'image', 'voice', 'other'])
export type CopyChange = z.infer<typeof CopyChange>

/** The wizard's view of any campaign (a draft to go on with; the summary the approver reads). */
export const CampaignDraft = CampaignDraftInput.extend({
  id: Id,
  status: CampaignStatus,
  made_by: Id,
  made_by_name: z.string(),
  copied_from: Id.nullable(),
  copied_from_name: z.string().nullable(),
  changed_from_copy: z.array(CopyChange),
  approved_at: IsoDateTime.nullable(),
})
export type CampaignDraft = z.infer<typeof CampaignDraft>

/** Why someone of the audience is not sent to (plan §5, A9); the count is what will really be sent. */
export const RemovedReason = z.enum([
  'stop',
  'suppression',
  'no_consent',
  'disbursal_block',
  'marketing_today',
  'in_other_campaign',
  'invalid_mobile',
  'over_people_limit',
  'over_meta_limit',
])
export type RemovedReason = z.infer<typeof RemovedReason>

export const AudienceCountInput = z.object({
  kind: CampaignKind,
  channels: z.array(WizardChannel),
  audience_id: Id.nullable(),
  schedule: CampaignSchedule.nullable(),
  people_limit: z.number().int().min(1).nullable(),
  include_no_consent: z.boolean().optional().describe('PD63: the no_consent reason is not applied'),
  numbers: CampaignNumbers,
  number_caps: NumberCaps,
  campaign_id: Id.nullable().describe('the draft itself: its own people are not "in another campaign"'),
})
export type AudienceCountInput = z.infer<typeof AudienceCountInput>

const count = z.number().int().min(0)
export const AudienceCount = z.object({
  total: count.describe('everyone the audience picks, before anything is removed'),
  removed: z.array(z.object({ reason: RemovedReason, count: z.number().int().min(1) })),
  will_send: count.describe('what will really be sent'),
  per_day: count.describe('all ticked numbers together, at their limits'),
  days: count.describe('about how many days at those limits'),
  split: z
    .array(z.object({ number_id: Id, display: z.string(), per_day: count }))
    .describe('round robin, in proportion to each number’s limit (A5)'),
})
export type AudienceCount = z.infer<typeof AudienceCount>

/** Admin only (R6): never the full mobile, never in a URL or a log. */
export const RemovedPerson = z.object({
  lead_code: LeadCode,
  mobile_masked: MaskedMobile.nullable().describe('null: the source has not given it yet'),
  reason: RemovedReason,
})
export type RemovedPerson = z.infer<typeof RemovedPerson>

/**
 * Meta's limit (A10, R4), a rolling 24 hours. W1: each WhatsApp account (WABA) has its own; this is all active
 * accounts together (an account that shares another's limit adds nothing; an unlimited one counts as 1,00,000).
 */
export const MetaLimit = z.object({
  limit: count.describe("Meta's tier: business-initiated conversations in a rolling 24 h"),
  used_24h: count.describe("every business-initiated WhatsApp sent in the last 24 h (the portal's and, W3, Exotel's own page)"),
  stop_at: count.describe('the portal stops here (the stop share of Settings, 95 % by default)'),
  left: count.describe('stop_at − used_24h, never below 0'),
})
export type MetaLimit = z.infer<typeof MetaLimit>

/** The made-up lead code of every preview and test WhatsApp. */
export const EXAMPLE_LEAD_CODE = 'FF-7KQ2M9XA'
/** PD64: the lender's reference of the example person (a test send has no lender answer). */
export const EXAMPLE_LENDER_REF = 'LX-7Q2M'

/** Owner 2026-10-29: the button's link carries our lead code as utm_content, on the lender's own UTM link and on
 *  Apply now (a link that is not a URL stays as it is). */
export function withLeadCode(url: string, leadCode: string): string {
  try {
    const link = new URL(url)
    link.searchParams.set('utm_content', leadCode)
    return link.toString()
  } catch {
    return url
  }
}

/** The test drawer: the step's content, the team mobile, the number it goes from, the words typed for each {{n}}
 *  (empty = the example person's) and the lender (its own link + utm_content, as the real send). */
export const TestWhatsAppInput = z.object({
  content: WhatsAppContent,
  mobile: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, 'A team mobile: 10 digits, starting 6–9'),
  number_id: Id.nullable().describe('null: the first connected WhatsApp number'),
  values: z
    .array(z.string().trim().max(60, 'At most 60 letters'))
    .max(5)
    .describe('{{1}} … {{5}} as typed in the test drawer; an empty one gets the example person’s'),
  lender: LenderContent.nullable(),
})
export type TestWhatsAppInput = z.infer<typeof TestWhatsAppInput>

/** An App admin campaign link the WhatsApp short link may open (app-api's GET /service/v1/campaign-links). */
export const AppLink = z.object({
  code: z.string().describe('as in its short link, e.g. BHT1F6'),
  name: z.string(),
  utm_campaign: z.string(),
  utm_source: z.string(),
  goes_to: z.enum(['default', 'lender']).describe("default = Apply now's start; lender = a lender's journey"),
  journey: z.string().nullable().describe('the journey it opens, as in /apply-now/<journey>'),
  short_url: z.string(),
})
export type AppLink = z.infer<typeof AppLink>
/** The links, or none and a note when App admin did not answer. */
export const AppLinks = z.object({ links: z.array(AppLink), note: z.string().nullable() })
export type AppLinks = z.infer<typeof AppLinks>

export const SaveAsWorkflowInput = z.object({
  name: z.string().trim().min(3, 'Give it a name people understand'),
  document: WorkflowDocument,
})
export type SaveAsWorkflowInput = z.infer<typeof SaveAsWorkflowInput>

export interface CampaignWizardApi {
  /** a new draft (status draft) made by the signed-in person */
  createCampaignDraft(input: CampaignDraftInput): Promise<{ id: string }>
  /** any campaign as the wizard sees it (an older one: its workflow's live version as the canvas) */
  getCampaignDraft(id: string): Promise<CampaignDraft>
  /** drafts only; the maker or an admin */
  saveCampaignDraft(id: string, input: CampaignDraftInput): Promise<CampaignDraft>
  /** checks every step again; then it waits for a second person's approval */
  submitCampaign(id: string): Promise<{ id: string }>
  deleteCampaignDraft(id: string): Promise<void>
  /** A11: a new draft with everything of that campaign; change audience, image, voice and send it again */
  copyCampaign(id: string): Promise<{ id: string }>
  countCampaignAudience(input: AudienceCountInput): Promise<AudienceCount>
  /** R6: admin only */
  listRemovedPeople(input: AudienceCountInput): Promise<RemovedPerson[]>
  getMetaLimit(): Promise<MetaLimit>
  /** sends the filled template to one team mobile (the mobile is never stored or logged); link: the short link in
   *  the button, opens: where it goes (utm_content = the lead code) */
  sendTestWhatsApp(input: TestWhatsAppInput): Promise<{ sent_to: string; link: string; opens: string }>
  /** App admin's campaign links (Apply now only) a WhatsApp's short link may open; none + a note when it cannot answer */
  listAppLinks(): Promise<AppLinks>
  /** the canvas becomes a new workflow with this document as its first draft (A4) */
  saveCanvasAsWorkflow(input: SaveAsWorkflowInput): Promise<{ workflow_id: string }>
}
